import { cn } from '@/utils';
import { FEATURE_FLAGS, STATUS_MESSAGES } from '@/constants';

const isV2 = FEATURE_FLAGS.ENABLE_SCORECARD_V2;

/**
 * SimulationComplianceBanner Component
 *
 * It includes an SVG icon to visually indicate a compliance violation and a text message
 * that can be customized via the `text` prop.
 */
export const SimulationComplianceBanner: React.FC<{ text?: string }> = ({
  text = STATUS_MESSAGES.COMPLIANCE_REMINDERS,
}) => {
  // ==================================================
  // Compliance Violation SVG Icon
  // ==================================================
  const Svg = () => (
    <div className="flex h-8 min-h-8 w-8 min-w-8 items-center justify-center">
      <svg
        aria-label="Violation visualization"
        fill="none"
        height="24"
        role="img"
        viewBox="0 0 24 24"
        width="24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill={isV2 ? 'var(--color-accent-border-yellow)' : 'var(--color-accent-red-500)'}
          d="M22.0617 10.9418L13.057 1.93619C12.776 1.65681 12.3958 1.5 11.9995 1.5C11.6033 1.5 11.2231 1.65681 10.942 1.93619L1.94205 10.9418C1.66267 11.2229 1.50586 11.603 1.50586 11.9993C1.50586 12.3956 1.66267 12.7758 1.94205 13.0568L10.9467 22.0624C11.2278 22.3418 11.608 22.4986 12.0042 22.4986C12.4005 22.4986 12.7807 22.3418 13.0617 22.0624L22.0664 13.0568C22.3458 12.7758 22.5026 12.3956 22.5026 11.9993C22.5026 11.603 22.3458 11.2229 22.0664 10.9418H22.0617ZM11.2495 7.49931C11.2495 7.3004 11.3286 7.10964 11.4692 6.96898C11.6099 6.82833 11.8006 6.74931 11.9995 6.74931C12.1985 6.74931 12.3892 6.82833 12.5299 6.96898C12.6705 7.10964 12.7495 7.3004 12.7495 7.49931V12.7493C12.7495 12.9482 12.6705 13.139 12.5299 13.2796C12.3892 13.4203 12.1985 13.4993 11.9995 13.4993C11.8006 13.4993 11.6099 13.4203 11.4692 13.2796C11.3286 13.139 11.2495 12.9482 11.2495 12.7493V7.49931ZM11.9995 17.2493C11.777 17.2493 11.5595 17.1833 11.3745 17.0597C11.1895 16.9361 11.0453 16.7604 10.9602 16.5548C10.875 16.3493 10.8528 16.1231 10.8962 15.9048C10.9396 15.6866 11.0467 15.4862 11.2041 15.3288C11.3614 15.1715 11.5618 15.0643 11.7801 15.0209C11.9983 14.9775 12.2245 14.9998 12.4301 15.0849C12.6356 15.1701 12.8113 15.3143 12.935 15.4993C13.0586 15.6843 13.1245 15.9018 13.1245 16.1243C13.1245 16.4227 13.006 16.7088 12.795 16.9198C12.5841 17.1308 12.2979 17.2493 11.9995 17.2493Z"
        />
      </svg>
    </div>
  );

  // ==================================================
  // Render
  // ==================================================
  return (
    <div
      className={cn(
        isV2 ? 'bg-accent-surface-yellow' : 'bg-accent-surface-red',
        isV2 ? 'border border-accent-border-yellow' : 'border border-accent-red-500',
        'flex flex-row items-start gap-1.5 sm:items-center',
        'w-full rounded-lg p-3',
      )}
    >
      <Svg />
      <div
        className={cn(
          'text-xs',
          'font-text font-bold',
          isV2 ? 'text-accent-border-yellow' : 'text-accent-red-500',
        )}
      >
        {text}
      </div>
    </div>
  );
};

SimulationComplianceBanner.displayName = 'SimulationComplianceBanner';

export default SimulationComplianceBanner;
